import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Coins, Tag, Gift, Award, Sparkles, ArrowUpRight, ArrowDownRight, Lock } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const DISCOUNT_KEY = "taven_discount_code";
const SPIN_KEY = "taven_spin";
const ICONS = { tag: Tag, gift: Gift, award: Award };

const REASON_LABELS = {
  spin_reward: "Spin & Win reward", coin_purchase: "Coins purchased", gift_sent: "Gift sent",
  gift_refund: "Gift refunded", reward_redeemed: "Reward redeemed",
};

export const RewardsPage = () => {
  const { user, authApi } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState("");

  const loadWallet = useCallback(() => {
    if (!user) return;
    authApi.get("/rewards/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, [user]);

  useEffect(() => { api.get("/rewards/catalog").then(({ data }) => setCatalog(data.catalog || [])); }, []);
  useEffect(() => { loadWallet(); }, [loadWallet]);

  const redeem = async (reward) => {
    if (!user) { toast.error("Please sign in to redeem rewards."); return; }
    if ((wallet?.balance ?? 0) < reward.coins) { toast.error("Not enough TAVEN Coins yet."); return; }
    setBusy(reward.key);
    try {
      const { data } = await authApi.post("/rewards/redeem", { reward_key: reward.key });
      setWallet((w) => ({ ...w, balance: data.balance }));
      const u = data.unlocked;
      if (u.type === "discount_code") {
        localStorage.setItem(DISCOUNT_KEY, u.code);
        toast.success(`${u.percent}% OFF unlocked! Code ${u.code} applies at product checkout.`);
      } else if (u.type === "bonus_spin") {
        try { const s = JSON.parse(localStorage.getItem(SPIN_KEY)) || {}; delete s.lastSpin; localStorage.setItem(SPIN_KEY, JSON.stringify(s)); } catch {}
        toast.success("Bonus spin unlocked — head to the homepage and spin again!");
      } else if (u.type === "certificate_voucher") {
        toast.success("Free certificate voucher saved to your account!");
      }
      loadWallet();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem.");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="rewards-page">
      <PageHero eyebrow="TAVEN Rewards" title="Rewards Wallet" subtitle="Earn TAVEN Coins from the daily Spin & Win, then redeem them for product-discount vouchers and perks. Discount vouchers apply to TAVEN products only.">
        <div className="flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2.5" data-testid="rewards-balance">
          <Coins className="w-5 h-5 text-taven-gold" /><span className="font-black font-display text-lg">{user ? (wallet?.balance ?? "—") : 0}</span><span className="text-xs uppercase tracking-wider text-white/70">coins</span>
        </div>
      </PageHero>

      <section className="py-10 px-4 md:px-8 lg:px-12 mx-auto max-w-5xl">
        {!user && (
          <div className="border border-taven-gold bg-taven-gold/10 p-5 mb-8 flex items-center justify-between flex-wrap gap-3" data-testid="rewards-signin-prompt">
            <p className="text-sm font-medium flex items-center gap-2"><Lock className="w-4 h-4 text-taven-gold" /> Sign in to view your balance and redeem rewards.</p>
            <Link to="/login" className="bg-taven-black text-white text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-taven-orange transition-colors">Sign in</Link>
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-black font-display">Redeem Your Coins</h2>
          <Link to="/coins" className="text-xs font-bold uppercase tracking-wider text-taven-gold hover:underline flex items-center gap-1">Buy more coins <ArrowUpRight className="w-3.5 h-3.5" /></Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {catalog.map((r) => {
            const Icon = ICONS[r.icon] || Sparkles;
            const affordable = (wallet?.balance ?? 0) >= r.coins;
            return (
              <div key={r.key} className="border border-black/10 bg-white p-5 flex flex-col" data-testid={`reward-${r.key}`}>
                <Icon className="w-8 h-8 text-taven-gold" />
                <p className="font-bold font-display mt-3">{r.name}</p>
                <p className="text-xs text-muted-foreground mt-1 flex-1">{r.desc}</p>
                <div className="flex items-center gap-1.5 mt-3 text-taven-gold font-black font-display"><Coins className="w-4 h-4" />{r.coins}</div>
                <button onClick={() => redeem(r)} disabled={!user || busy === r.key || !affordable}
                  data-testid={`redeem-${r.key}`}
                  className="mt-3 w-full bg-taven-orange text-white text-xs font-bold uppercase tracking-wider py-2.5 hover:bg-[#d9561a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {busy === r.key ? "Redeeming…" : !user ? "Sign in" : affordable ? "Redeem" : "Not enough coins"}
                </button>
              </div>
            );
          })}
        </div>

        {user && wallet && (
          <div className="mt-12">
            <h2 className="text-2xl font-black font-display mb-4">Coin Activity</h2>
            <div className="border border-black/10 bg-white divide-y divide-black/5" data-testid="rewards-ledger">
              {(wallet.ledger || []).length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No coin activity yet. Spin the wheel on the homepage to start earning!</p>}
              {(wallet.ledger || []).map((e) => (
                <div key={e.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3">
                    {e.delta > 0 ? <ArrowUpRight className="w-4 h-4 text-green-600" /> : <ArrowDownRight className="w-4 h-4 text-taven-orange" />}
                    <div>
                      <p className="text-sm font-medium">{REASON_LABELS[e.reason] || e.reason}</p>
                      <p className="text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <span className={`font-black font-display ${e.delta > 0 ? "text-green-600" : "text-taven-orange"}`}>{e.delta > 0 ? "+" : ""}{e.delta}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-8">Discount vouchers apply to TAVEN products only — never to course, certificate, affiliate or seller payments. TAVEN Coins are voluntary digital rewards with no cash value. Support: support@taven.store</p>
      </section>
    </div>
  );
};

export default RewardsPage;
